import MayaAvatar from '../assets/employees/v2/maya.png';
import AnnaAvatar from '../assets/employees/v2/anna.png';
import AlexAvatar from '../assets/employees/v2/alex.png';
import OscarAvatar from '../assets/employees/v2/oscar.png';
import EmmaAvatar from '../assets/employees/v2/emma.png';
import IrisAvatar from '../assets/employees/v2/iris.png';
import CodyAvatar from '../assets/employees/v2/cody.png';
import WesAvatar from '../assets/employees/v2/wes.png';
// Card background layer (semicircle halo + floating chips). Layered BEHIND the
// person avatar in the card — kept as a separate image (no compositing) so both
// stay sharp and aligned, exactly as exported from the design.
import MayaBg from '../assets/employees/v2/bg/maya.png';
import AnnaBg from '../assets/employees/v2/bg/anna.png';
import AlexBg from '../assets/employees/v2/bg/alex.png';
import OscarBg from '../assets/employees/v2/bg/oscar.png';
import EmmaBg from '../assets/employees/v2/bg/emma.png';
import IrisBg from '../assets/employees/v2/bg/iris.png';
import CodyBg from '../assets/employees/v2/bg/cody.png';
import WesBg from '../assets/employees/v2/bg/wes.png';
// Clean face cutouts (transparent headshots) for circular avatars (roster, sidebar).
import MayaFace from '../assets/employees/v2/face/maya.png';
import AnnaFace from '../assets/employees/v2/face/anna.png';
import AlexFace from '../assets/employees/v2/face/alex.png';
import OscarFace from '../assets/employees/v2/face/oscar.png';
import EmmaFace from '../assets/employees/v2/face/emma.png';
import IrisFace from '../assets/employees/v2/face/iris.png';
import CodyFace from '../assets/employees/v2/face/cody.png';
import WesFace from '../assets/employees/v2/face/wes.png';

/**
 * The 8 AI Team employees, keyed by the backend agent-type code
 * ({@link CreditPlanCatalog} on chatbot-api).
 *
 * Each agent unlocks at a plan `tier` (1=Starter … 4=Scale). `launched` agents
 * (Maya/Anna/Alex) are usable today; the rest render as "Join your team soon".
 * Lock/active state per card is derived from the user's plan tier vs `tier`
 * (see AiTeamLandingPage). `accent` is the soft halo behind the portrait.
 */
export interface CreditAgent {
  /** Backend agent-type code (matches CreditPlanCatalog.allowedAgents). */
  agentType: string;
  name: string;
  role: string;
  desc: string;
  /** Person cutout — the front layer of the card + small circular face elsewhere. */
  avatar: string;
  /** Card background layer (semicircle + chips), drawn behind the person. */
  bg: string;
  /** Clean face cutout for circular avatars (roster/sidebar). */
  face: string;
  /** Person width as % of card width (= person px / bg px) so it sits in the halo. */
  personPct: number;
  /** Plan tier that unlocks this agent: 1=Starter, 2=Growth, 3=Pro, 4=Scale. */
  tier: number;
  /** Functional today? Non-launched agents show "Join your team soon". */
  launched: boolean;
  /** Requires onboarding setup before going live (Anna). */
  setupGated?: boolean;
  /** Two CSS colors for the soft semicircle halo behind the portrait. */
  accent: [string, string];
  /** Route opened by the active "Open" button. */
  route?: string;
}

export const CREDIT_AGENTS: CreditAgent[] = [
  {
    agentType: 'marketing',
    name: 'Maya',
    role: 'Marketing Specialist',
    desc: 'Writes SEO blogs, product copy & social posts in Content Studio, then publishes to Shopify.',
    avatar: MayaAvatar,
    bg: MayaBg,
    face: MayaFace,
    personPct: 100,
    tier: 1,
    launched: true,
    accent: ['#d1fae5', '#a7f3d0'],
    route: '/ai-team/marketing/studio',
  },
  {
    agentType: 'customer_support',
    name: 'Anna',
    role: 'Customer Support Specialist',
    desc: '24/7 Shopify web chat — answers FAQs, order & shipping questions and returns.',
    avatar: AnnaAvatar,
    bg: AnnaBg,
    face: AnnaFace,
    personPct: 100,
    tier: 2,
    launched: true,
    setupGated: true,
    accent: ['#fce7f3', '#fbcfe8'],
    route: '/ai-team/anna',
  },
  {
    agentType: 'sales',
    name: 'Alex',
    role: 'Social Sales Specialist',
    desc: 'Auto-replies across Messenger, Instagram, WhatsApp & Email; captures & converts leads.',
    avatar: AlexAvatar,
    bg: AlexBg,
    face: AlexFace,
    personPct: 100,
    tier: 3,
    launched: true,
    accent: ['#dbeafe', '#bfdbfe'],
    route: '/ai-team/alex',
  },
  {
    agentType: 'operation',
    name: 'Oscar',
    role: 'Operation Specialist',
    desc: 'Tracks inventory, monitors stock & order status, and answers operations questions in real time.',
    avatar: OscarAvatar,
    bg: OscarBg,
    face: OscarFace,
    personPct: 100,
    // Pro, not Scale (Monica, 2026-08-12). chatbot-api's CreditPlanCatalog puts `operation` in
    // ai_pro.allowedAgents — the list that actually provisions agents — so a Pro merchant was
    // served Oscar while this card told them to upgrade to Scale.
    tier: 3,
    launched: true,
    accent: ['#ede9fe', '#ddd6fe'],
  },
  {
    agentType: 'email_sms',
    name: 'Emma',
    role: 'Email & SMS Specialist',
    desc: 'Runs automated email & SMS flows — abandoned cart, win-back and post-purchase campaigns.',
    avatar: EmmaAvatar,
    bg: EmmaBg,
    face: EmmaFace,
    personPct: 100,
    tier: 4,
    launched: false,
    accent: ['#fce7f3', '#fbcfe8'],
  },
  {
    agentType: 'analytics',
    name: 'Iris',
    role: 'Insights Analyst',
    desc: 'Analyzes sales, margins & trends, turning your store data into clear, actionable insights.',
    avatar: IrisAvatar,
    bg: IrisBg,
    face: IrisFace,
    personPct: 100,
    tier: 4,
    launched: false,
    accent: ['#e0e7ff', '#c7d2fe'],
  },
  {
    agentType: 'seo',
    name: 'Cody',
    role: 'SEO Specialist',
    desc: 'Optimizes product pages & content to improve search rankings and visibility on Shopify.',
    avatar: CodyAvatar,
    bg: CodyBg,
    face: CodyFace,
    personPct: 100,
    tier: 4,
    launched: false,
    accent: ['#dcfce7', '#bbf7d0'],
  },
  {
    agentType: 'website_builder',
    name: 'Wes',
    role: 'Website Builder',
    desc: 'Builds & updates landing pages and storefront sections — no code, on-brand by default.',
    avatar: WesAvatar,
    bg: WesBg,
    face: WesFace,
    personPct: 100,
    tier: 4,
    launched: false,
    accent: ['#fef3c7', '#fde68a'],
  },
];

/** Plan tier ladder, shared by the AI Team page + sidebar gating. */
export const PLAN_TIER: Record<string, number> = { starter: 1, growth: 2, pro: 3, scale: 4 };
export const PLAN_LABEL: Record<string, string> = {
  starter: 'Core',
  growth: 'Growth',
  pro: 'Pro',
  scale: 'Scale',
};
export const planKeyForTier = (tier: number): string =>
  Object.keys(PLAN_TIER).find(k => PLAN_TIER[k] === tier) ?? 'scale';
